import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import DoneIcon from '@mui/icons-material/Done';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import HistoryIcon from '@mui/icons-material/History';

const WorkflowHistory = ({ orderId }) => {
  const [history, setHistory] = useState([]);
  const [updateOrders, setUpdateOrders] = useState([]);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  // Fetch real workflow history data and update orders
  useEffect(() => {
    if (!orderId || !token) return;
    
    const fetchWorkflowHistory = async () => {
      try {
        
        // Fetch status history
        const response = await axios.get(`${config.apiUrl}/api/ad-orders/${orderId}/history`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          if (response.data.data.length > 0) {
            
            // Remove duplicates based on status, notes, and created_at
            const uniqueHistory = response.data.data.filter((item, index, self) => {
              return index === self.findIndex(t => (
                t.status === item.status &&
                t.notes === item.notes &&
                t.created_at === item.created_at &&
                t.user_name === item.user_name
              ));
            });
            setHistory(uniqueHistory);
          } else {
            // Create a basic history entry based on order creation
            const defaultHistory = [
              {
                id: null,
                order_id: orderId,
                status: 'New',
                notes: 'Order created',
                created_at: new Date().toISOString(),
                user_name: 'System'
              }
            ];
            setHistory(defaultHistory);
          }
        } else {
          console.warn(`⚠️ Invalid response format for order ${orderId} history:`, response.data);
          setError('Invalid response format from server');
        }

        // Fetch related update orders
        try {
          const updateResponse = await axios.get(`${config.apiUrl}/api/update-orders`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (updateResponse.data && updateResponse.data.success && Array.isArray(updateResponse.data.data)) {
            // Filter update orders related to this order
            const relatedUpdates = updateResponse.data.data.filter(update => 
              update.relatedOrderId === parseInt(orderId)
            );
            setUpdateOrders(relatedUpdates);
          }
        } catch (updateError) {
          console.error(`Failed to fetch update orders:`, updateError);
          // Don't set error state, just log it
        }
      } catch (apiError) {
        console.error(`❌ Failed to fetch history for order ${orderId}:`, apiError);
        setError(`Failed to load workflow history: ${apiError.message}`);
        
        // Set a basic fallback history instead of mock data
        const fallbackHistory = [
          {
            id: null,
            order_id: orderId,
            status: 'New',
            notes: 'Order created',
            created_at: new Date().toISOString(),
            user_name: 'System'
          }
        ];
        setHistory(fallbackHistory);
      }
    };
    
    fetchWorkflowHistory();
  }, [orderId, token]);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return <HistoryIcon color="action" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'approved':
        return <CheckCircleIcon color="success" />;
      case 'rejected':
        return <CancelIcon color="error" />;
      case 'completed':
        return <DoneIcon color="info" />;
      case 'in design':
        return <PendingIcon color="info" />;
      case 'proofing':
        return <PendingIcon color="primary" />;
      case 'waiting for artwork':
        return <PauseCircleIcon color="warning" />;
      case 'uploaded':
        return <DoneIcon color="info" />;
      case 'invoiced':
        return <DoneIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'on hold':
        return <PauseCircleIcon color="secondary" />;
      default:
        return <HistoryIcon color="action" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return 'default';
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'completed':
        return 'success';
      case 'in design':
        return 'info';
      case 'proofing':
        return 'primary';
      case 'waiting for artwork':
        return 'warning';
      case 'uploaded':
        return 'info';
      case 'invoiced':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'on hold':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU');
  };

  // Combine history and update orders, sort by date
  const combinedHistory = [
    ...history.map(h => ({ ...h, type: 'status' })),
    ...updateOrders.map(u => ({ ...u, type: 'update', created_at: u.createdAt }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Order History
      </Typography>
      
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {combinedHistory.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No history available for this order.
        </Typography>
      ) : (
        <List>
          {combinedHistory.map((item, index) => (
            <React.Fragment key={item.id || `history-${index}`}>
              {item.type === 'status' ? (
                <ListItem alignItems="flex-start">
                  <ListItemIcon>
                    {getStatusIcon(item.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <React.Fragment>
                        <Chip 
                          label={item.status} 
                          color={getStatusColor(item.status)}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="caption" color="textSecondary" component="span">
                          {formatDate(item.created_at)}
                        </Typography>
                      </React.Fragment>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2">
                          {item.notes}
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                          By: {item.user_name || item.user || 'System'}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ) : (
                <ListItem alignItems="flex-start" sx={{ bgcolor: 'action.hover' }}>
                  <ListItemIcon>
                    <HistoryIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <React.Fragment>
                        <Chip 
                          label="Update Order" 
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Chip 
                          label={item.orderUpdateType || 'Order Update'} 
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="caption" color="textSecondary" component="span">
                          {formatDate(item.created_at)}
                        </Typography>
                      </React.Fragment>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                          Update Order #{item.id} created
                        </Typography>
                        {item.notes && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {item.notes.split('\n')[0]}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                          By: {item.createdByName || 'System'}
                        </Typography>
                        {item.totalPrice > 0 && (
                          <Typography component="span" variant="caption" sx={{ display: 'block', color: 'success.main', mt: 0.5, fontWeight: 'bold' }}>
                            Cost: ${parseFloat(item.totalPrice).toFixed(2)}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              )}
              {index < combinedHistory.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

WorkflowHistory.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default WorkflowHistory; 